import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { BetaDisclaimerContent } from '@/types/api';

interface BetaDisclaimerModalProps {
  isOpen: boolean;
  content: BetaDisclaimerContent;
  isSubmitting: boolean;
  error: string | null;
  onAccept: () => void;
}

// Intentionally undismissable: there is no close button and onClose is a no-op,
// so clicking the backdrop or pressing Escape does nothing. The only way out is
// to explicitly accept.
const noop = () => {};

export function BetaDisclaimerModal({
  isOpen,
  content,
  isSubmitting,
  error,
  onAccept,
}: BetaDisclaimerModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={noop}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform overflow-hidden rounded-2xl border bg-white p-6 shadow-xl transition-all dark:border-white/[0.08] dark:bg-abyss-100/95 dark:shadow-glow-violet dark:backdrop-blur-xl">
                {/* top accent */}
                <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-px bg-gradient-to-r from-transparent via-violet-400/70 to-transparent dark:block" />

                <div className="mb-3 flex items-center gap-2.5">
                  <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-amber-400" aria-hidden="true" />
                  <Dialog.Title className="font-display text-xl font-semibold text-gray-900 dark:text-white">
                    {content.title}
                  </Dialog.Title>
                </div>

                <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">{content.intro}</p>

                <ul className="mb-5 space-y-2.5">
                  {content.points.map((point, index) => (
                    <li key={index} className="flex gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                      <span
                        className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-violet-400"
                        aria-hidden="true"
                      />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>

                <p className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm font-medium text-gray-800 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-gray-200">
                  {content.acknowledgement}
                </p>

                {error && (
                  <div className="mb-3 rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={onAccept}
                  disabled={isSubmitting}
                  className="btn-aurora w-full !py-3"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <div className="spinner h-4 w-4" />
                      Recording…
                    </span>
                  ) : (
                    'I understand and accept'
                  )}
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
